import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Logger } from '../logger.service';
import { firstValueFrom } from 'rxjs';
import { CredentialsService } from '@app/auth';
import { AuthService } from './auth.service';

const log = new Logger('http-service');

interface ResponseData {
  code: string;
  message: string;
  data: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  headers?: {
    Authorization: string;
    'Content-Type': string;
  };

  constructor(private http: HttpClient, private credentialsS: CredentialsService, private authS: AuthService) {
    // const credentials = this.credentialsS.credentials;
    // if (credentials) {
    //   log.debug('Credentials found: ', credentials);
    //   this.headers = {
    //     Authorization: `Bearer ${credentials.token}`,
    //     'Content-Type': 'application/json',
    //   };
    // } else {
    //   console.log('No credentials found: ', credentials);
    //   this.headers = {
    //     Authorization: `Bearer ${this.authS.updateToken()}`,
    //     'Content-Type': 'application/json',
    //   };
    // }
    this.initHeaders();
  }

  private isTokenExpired(token: string): boolean {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return true;
      const payloadDecoded = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
      const payload = JSON.parse(payloadDecoded);
      if (payload && typeof payload.exp === 'number') {
        return payload.exp * 1000 - 10000 < Date.now();
      }
    } catch (e) {
      return true;
    }
    return true;
  }

  private async initHeaders(forceRefresh: boolean = false) {
    const credentials = this.credentialsS.credentials;
    const isExpired = credentials && credentials.token ? this.isTokenExpired(credentials.token) : true;

    if (credentials && credentials.token && !forceRefresh && !isExpired) {
      this.headers = {
        Authorization: `Bearer ${credentials.token}`,
        'Content-Type': 'application/json',
      };
      // No actualizar token si ya tenemos uno válido
      return;
    }

    await this.updateToken(forceRefresh || isExpired);
    const updatedCredentials = this.credentialsS.credentials;
    if (updatedCredentials && updatedCredentials.token) {
      this.headers = {
        Authorization: `Bearer ${updatedCredentials.token}`,
        'Content-Type': 'application/json',
      };
    } else {
      this.headers = undefined;
    }
  }

  async updateToken(forceRefresh: boolean = false) {
    const token = await this.authS.updateToken(forceRefresh);

    if (token) {
      this.headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
    }
  }

  /**
   * Método para peticiones críticas que requieren token fresco (casino, book)
   * Solo fuerza renovación si es realmente necesario
   */
  async postWithFreshToken(
    url: string,
    body: any,
    params?: {
      [param: string]: string | string[];
    }
  ): Promise<ResponseData | null> {
    try {
      // Forzar renovación del token solo para peticiones críticas
      await this.initHeaders(true);

      const response = (await firstValueFrom(
        this.http.post(url, body, {
          headers: this.headers,
          params,
        })
      )) as ResponseData;
      return response;
    } catch (error) {
      log.error(error);
    }
    return null;
  }

  async post(
    url: string,
    body: any,
    params?: {
      [param: string]: string | string[];
    }
  ): Promise<ResponseData | null> {
    try {
      // Actualizar token sin forzar renovación (Firebase maneja automáticamente si está expirado)
      await this.initHeaders(false);

      const response = (await firstValueFrom(
        this.http.post(url, body, {
          headers: this.headers,
          params,
        })
      )) as ResponseData;
      return response;
    } catch (error) {
      log.error(error);
    }
    return null;
  }

  async postErrorFixed(
    url: string,
    body: any,
    params?: {
      [param: string]: string | string[];
    }
  ): Promise<ResponseData | null | string> {
    try {
      // Actualizar token sin forzar renovación (Firebase maneja automáticamente si está expirado)
      await this.initHeaders(false);

      const response = (await firstValueFrom(
        this.http.post(url, body, {
          headers: this.headers,
          params,
        })
      )) as ResponseData;
      return response;
    } catch (error) {
      log.error(error);
      return ((error as HttpErrorResponse).error?.code as string) || null;
    }
    return null;
  }

  async get(
    url: string,
    params?: {
      [param: string]: string;
    }
  ): Promise<ResponseData | null> {
    try {
      // Actualizar token antes de la petición (sin forzar renovación para GET)
      await this.initHeaders(false);

      const response = (await firstValueFrom(
        this.http.get(url, {
          headers: this.headers,
          params,
        })
      )) as ResponseData;
      return response;
    } catch (error) {
      log.error(error);
    }
    return null;
  }

  async put(
    url: string,
    body: any,
    params?: {
      [param: string]: string | string[];
    }
  ): Promise<ResponseData | null> {
    try {
      // Actualizar token sin forzar renovación (Firebase maneja automáticamente si está expirado)
      await this.initHeaders(false);

      const response = (await firstValueFrom(
        this.http.put(url, body, {
          headers: this.headers,
          params,
        })
      )) as ResponseData;
      return response;
    } catch (error) {
      log.error(error);
    }
    return null;
  }

  async delete(
    url: string,
    params?: {
      [param: string]: string | string[];
    }
  ): Promise<ResponseData | null> {
    try {
      // Actualizar token antes de la petición
      await this.initHeaders(false);

      const response = (await firstValueFrom(
        this.http.delete(url, {
          headers: this.headers,
          params,
        })
      )) as ResponseData;
      return response;
    } catch (error) {
      log.error(error);
    }
    return null;
  }

  async postWithException(
    url: string,
    body: any,
    params?: {
      [param: string]: string | string[];
    }
  ): Promise<ResponseData> {
    await this.initHeaders(false);
    return (await firstValueFrom(
      this.http.post(url, body, {
        headers: this.headers,
        params,
      })
    )) as ResponseData;
  }
}
